import { ComplianceTriangle, FineImage } from './shared/ComplianceBits';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { readAllNotifications, readNotification, useNotificationCounts } from '../hooks/useNotificationCounts';
import { useMessageRenderer } from '../lib/i18n';
import { notificationArrived } from '../lib/notificationEvents';
import {
  CATEGORY_STYLES,
  getNotifications,
  relativeTime,
  resolveNotificationLink,
  type NotificationRow,
} from '../lib/notifications';

/**
 * The notification bell in the top bar. `openRequest` lets the Home page open
 * it: each change to the number opens the panel.
 */

const PANEL_WIDTH = 380;
const LATEST_COUNT = 8;

const CategoryDot: React.FC<{ category: NotificationRow['category'] }> = ({ category }) => {
  const style = CATEGORY_STYLES[category] ?? CATEGORY_STYLES.event;
  return (
    <span
      title={style.label}
      style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: style.bg, color: style.color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 600, letterSpacing: '0.02em',
      }}
    >
      {style.label.charAt(0)}
    </span>
  );
};

const NotificationBell: React.FC<{ openRequest?: number }> = ({ openRequest = 0 }) => {
  const navigate = useNavigate();
  const { unread } = useNotificationCounts();
  const renderMessage = useMessageRenderer();

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await getNotifications(LATEST_COUNT, 0, false));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  // Position the panel against the trigger, clamped to the viewport.
  useEffect(() => {
    if (!open || !buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    // Hangs from the button's right edge, since the bell sits at the end of the bar.
    const left = Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 12);
    setAnchor({ top: rect.bottom + 8, left: Math.max(12, left) });
  }, [open]);

  useEffect(() => {
    if (openRequest === 0) return;
    setOpen(true);
    load();
  }, [openRequest, load]);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // A notification that arrives while the panel is open joins the top of the list.
  useEffect(() => notificationArrived.subscribe((notification) => {
    setItems((current) => [
      notification,
      ...current.filter((item) => item.id !== notification.id),
    ].slice(0, LATEST_COUNT));
  }), []);

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) load();
  }

  function openItem(item: NotificationRow) {
    setOpen(false);
    if (!item.is_read) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      // Navigation matters more than the read flag. On a failure the count is
      // re-fetched and the list reloads the next time the bell opens.
      readNotification(item.id).catch(() => undefined);
    }
    const target = resolveNotificationLink(item.link);
    if (target) navigate(target);
  }

  async function handleMarkAll() {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    try {
      await readAllNotifications();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not mark all as read');
    }
  }

  const badge = unread > 99 ? '99+' : String(unread);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={toggle}
        title={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
        style={{
          position: 'relative',
          width: 40, height: 40, borderRadius: 12,
          border: '1px solid var(--border-subtle)',
          background: open ? 'rgba(75,166,234,0.08)' : '#fff',
          color: open ? '#3A74C2' : '#6b7280',
          cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 140ms ease',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
          />
          <path d="M13.7 21a2 2 0 01-3.4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>

        {unread > 0 && (
          <span
            key={unread}
            className="motion-safe:animate-[badge-bounce_380ms_ease-out]"
            style={{
              position: 'absolute', top: -5, right: -5,
              minWidth: 17, height: 17, padding: '0 4px',
              borderRadius: 9, background: '#ef4444', color: '#fff',
              fontSize: 10, fontWeight: 600, lineHeight: '17px',
              textAlign: 'center', border: '2px solid #fff',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {badge}
          </span>
        )}
      </button>

      {open && anchor && ReactDOM.createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Notifications"
          style={{
            position: 'fixed', top: anchor.top, left: anchor.left, zIndex: 3000,
            width: PANEL_WIDTH, maxWidth: 'calc(100vw - 24px)',
            background: '#fff', borderRadius: 14, border: '1px solid var(--border-subtle)',
            boxShadow: '0 16px 48px rgba(0,0,0,0.16)',
            overflow: 'hidden', animation: 'nbFade 140ms ease',
          }}
        >
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, padding: '13px 16px', borderBottom: '1px solid #f1f1f1',
          }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f1117' }}>
              Notifications
              {unread > 0 && (
                <span style={{ marginLeft: 7, fontSize: 11, fontWeight: 600, color: '#ef4444' }}>
                  {unread} new
                </span>
              )}
            </div>
            {unread > 0 && (
              <button
                onClick={handleMarkAll}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: '#3A74C2',
                  padding: 0, fontFamily: 'inherit',
                }}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {loading && (
              <div style={{ padding: '26px 16px', textAlign: 'center', fontSize: 12.5, color: '#9ca3af' }}>
                Loading…
              </div>
            )}

            {!loading && error && (
              <div style={{ padding: '20px 16px', fontSize: 12.5, color: '#ef4444', lineHeight: 1.6 }}>
                {error}
              </div>
            )}

            {!loading && !error && items.length === 0 && (
              <div style={{ padding: '30px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>You are all caught up</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  New notifications will show up here.
                </div>
              </div>
            )}

            {!loading && !error && items.map((item) => (
              <button
                key={item.id}
                onClick={() => openItem(item)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%',
                  padding: '12px 16px', border: 'none', textAlign: 'left',
                  borderBottom: '1px solid #f6f6f6',
                  background: item.is_read ? '#fff' : 'rgba(75,166,234,0.045)',
                  cursor: resolveNotificationLink(item.link) ? 'pointer' : 'default',
                  fontFamily: 'inherit',
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#fafbfc'; }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.background =
                    item.is_read ? '#fff' : 'rgba(75,166,234,0.045)';
                }}
              >
                {item.fine_alert ? <FineImage size={32} /> : item.compliance_tone
                  ? <ComplianceTriangle tone={item.compliance_tone} size={28} />
                  : <CategoryDot category={item.category} />}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 12.8, fontWeight: item.is_read ? 500 : 600,
                    color: item.fine_alert ? '#B91C1C' : '#0f1117', lineHeight: 1.4,
                  }}>
                    {renderMessage(item.i18n_key, item.vars, item.title)}
                  </div>
                  {renderMessage(item.body_i18n_key, item.vars, item.body) && (
                    <div style={{
                      fontSize: 12, color: '#6b7280', marginTop: 2, lineHeight: 1.5,
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {renderMessage(item.body_i18n_key, item.vars, item.body)}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                    {relativeTime(item.created_at)}
                  </div>
                </div>

                {!item.is_read && (
                  <span style={{
                    width: 7, height: 7, borderRadius: '50%', background: '#4ba6ea',
                    flexShrink: 0, marginTop: 6,
                  }} />
                )}
              </button>
            ))}
          </div>

          <button
            onClick={() => { setOpen(false); navigate('/dashboard/notifications'); }}
            style={{
              display: 'block', width: '100%', padding: '12px 16px',
              border: 'none', borderTop: '1px solid #f1f1f1', background: '#fafbfc',
              fontSize: 12.5, fontWeight: 600, color: '#3A74C2',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            View all notifications
          </button>

          <style>{`@keyframes nbFade { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>,
        document.body,
      )}
    </>
  );
};

export default NotificationBell;
